import React from "react";
import OutOfStockDialog from "./outofStockDialog";
import SignUpSuccessDialog from "./signUpSuccessDialog";
import { createRoot, Root } from "react-dom/client";
import JDILogin from "@/app/login/index.login";
import { fetchUserCenterAccount } from "@/app/csr-src/apis/profile/profile.api";
import { getTranslatedCookiesSettings } from "@/app/csr-src/data/consts/accountEnum";
import AccountSetDialog from "@/app/csr-src/pages/profile/personalCenter/personalAccountSetDialog";
import { outOfStockNotify } from "@/apis/outofStock/outofStock.api";
import UKToast from "@/components/UKToast";

interface VerifyParams {
  defaultFlag: string;
  defaultDialogFlag: string;
  defaultDialogTitleKey: string;
}

interface OutOfStockProps {
  email: string;
  skuIds: string[];
  isLoggedIn: boolean;
  onSuccess?: () => void;
  onFail?: () => void;
}

interface SignUpSuccessProps {
  email: string;
  onSuccess?: () => void;
}

export default class OutOfStackController {
  private root: Root | null = null;
  private element: HTMLElement | null = null;
  private isOpen: boolean = false;
  private isOpenSuccess: boolean = false;
  private isOpenVerify: boolean = false;
  private handleClose: () => void;
  private static instance: OutOfStackController;
  private verifyParams: VerifyParams;
  private addEmailParams: VerifyParams;

  constructor() {
    this.element = document.createElement("div");
    document.body.appendChild(this.element);
    this.handleClose = () => {
      this.isOpen = false;
      this.isOpenSuccess = false;
      this.isOpenVerify = false;
      if (this.root) {
        this.root.unmount();
        this.root = null;
      }
      if (this.element && document.body.contains(this.element)) {
        document.body.removeChild(this.element);
      }
    };
    let translationSetting = getTranslatedCookiesSettings(() => {});
    this.verifyParams = {
      defaultFlag: translationSetting.AccountTypeEnum.EMAIL_UNVERIFIED,
      defaultDialogFlag: translationSetting.AccountTypeEnum.EMAIL_VERIFICATION,
      defaultDialogTitleKey: "settings.emailInfo.emailUnverified",
    };
    this.addEmailParams = {
      defaultFlag: translationSetting.AccountTypeEnum.ADD_EMAIL,
      defaultDialogFlag: translationSetting.AccountTypeEnum.EMAIL_VERIFICATION,
      defaultDialogTitleKey: "settings.emailInfo.addEmail",
    };
  }

  static getInstance(): OutOfStackController {
    if (!this.instance) {
      this.instance = new OutOfStackController();
    }
    return this.instance;
  }

  async openOutOfStockDialog(skuIds :string[], onSuccess?: () => void, onFail?: () => void) {
    if (this.isOpen)
      return;
    let email = "";
    const { isLoggedIn } = await JDILogin.getInstance().getUserInfo();
    if (isLoggedIn) {
      const res = await fetchUserCenterAccount([6]);
      if (res && res.data && res.code === "200") {
        const { userInfo } = res.data;
        email = userInfo?.email;
        if(email && !!userInfo?.emailYn) {
          const res = await outOfStockNotify(email, skuIds);
          if(res && res.data){
            this.openSignUpSuccessDialog({ email, onSuccess });
          }
          else{
            onFail && onFail();
            if(res?.msg){
              UKToast.show({
                icon: "error",
                content: res?.msg,
              })
            }
          }
          return;
        }
      }
    }
    const param = {
      email,
      skuIds,
      isLoggedIn,
      onFail,
      onSuccess,
    }
    this.openSignUpDialog(param);
  }

  async openSignUpDialog(params: OutOfStockProps) {
    this.isOpen = true;
    try {
      if (!this.root && this.element) {
        this.root = createRoot(this.element);
      }
      if (this.root) {
        this.root.render(
          React.createElement(OutOfStockDialog, {
            ...params,
            open: true,
            closeFn: this.handleClose,
          })
        );
      }
    } catch (error) {
      this.isOpen = false;
      console.error("Failed to render outofstack dialog:", error);
    }
  }

  async openSignUpSuccessDialog(params: SignUpSuccessProps) {
    if (this.isOpenSuccess)
      return;
    this.isOpenSuccess = true;
    try {
      if (!this.root && this.element) {
        this.root = createRoot(this.element);
      }
      if (this.root) {
        this.root.render(
          React.createElement(SignUpSuccessDialog, {
            ...params,
            open: this.isOpenSuccess,
            closeFn: this.handleClose,
          })
        );
      }
    } catch (error) {
      this.isOpenSuccess = false;
      console.error("Failed to render sign up dialog:", error);
    }
  }

  async openVerifyEmailDialog(email: string, addEmail: boolean = false) {
    if (this.isOpenVerify)
      return;
    this.isOpenVerify = true;
    try {
      if (!this.root && this.element) {
        this.root = createRoot(this.element);
      }
      if (this.root) {
        this.root.render(
          React.createElement(AccountSetDialog, {
            open: this.isOpenVerify,
            flagParams: addEmail ? this.addEmailParams : this.verifyParams,
            autoVerifyEmail: email,
            onlyClose: this.handleClose,
          })
        );
      }
    } catch (error) {
      this.isOpenVerify = false;
      console.error("Failed to render verify email dialog:", error);
    }
  }
}